import React, { useContext, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { BufferService, LoadingContext, M_Button, M_Fieldset } from 'src/M_Components'
import { fields } from 'src/components/utils/fields'
import { Dialog } from 'primereact/dialog/dialog.cjs.js'
import { GroupWorkOutlined } from '@mui/icons-material'

/**
 * MovementGroupDialog
 *
 * Shared dialog for two operations:
 *   mode='create' — Add Group Movement from EquipmentAndMovementList
 *     - Equipment section disabled — pre-populated from selected equipment rows
 *     - Location fields editable — required for new movement creation
 *     - Calls POST /api/v1/Movement/CreateGroupOf with EquipmentId as list of selected IDs
 *
 *   mode='update' — Change Group Movement from LastMovementList / RangeMovementList
 *     - Equipment fields disabled — shows '*' (permanently locked, identity fields)
 *     - Location Name fields disabled — shows '*' (display-only, derived from code)
 *     - All other fields editable — prefilled using common value if all rows agree, '*' if mixed
 *     - Required fields highlighted in red if missing on save attempt
 *     - Calls PUT /api/v1/Movement/UpdateGroup/{idList} via GroupMovementUpdateRequest DTO
 *
 * Props:
 *   open            — boolean, controls dialog visibility
 *   setOpen         — setter for open
 *   mode            — 'create' | 'update'
 *   selectedRows    — array of selected equipment rows (create) or movement rows (update)
 *   onSuccess       — callback after successful save
 */
export const MovementGroupDialog = ({ open, setOpen, mode, selectedRows, onSuccess }) => {
    const { t } = useTranslation()
    const ctx = useContext(LoadingContext)

    const isCreate = mode === 'create'
    const isUpdate = mode === 'update'

    // ─── Prefill helpers ──────────────────────────────────────────────────────────
    // common(fn)   — returns the shared value if all rows agree, otherwise '*'
    // commonId(fn) — returns the shared id if all rows agree, otherwise null
    // Used in update mode so the user sees the current value when all movements
    // share it, and '*' when they differ (indicating a bulk overwrite will occur).

    const common = (fn) => {
        if (!selectedRows || selectedRows.length === 0) return ''
        const values = selectedRows.map(fn)
        return values.every(v => v === values[0]) ? (values[0] ?? '') : '*'
    }

    const commonId = (fn) => {
        if (!selectedRows || selectedRows.length === 0) return null
        const values = selectedRows.map(fn)
        return values.every(v => v === values[0]) ? (values[0] ?? null) : null
    }

    const commonBool = (fn) => {
        if (!selectedRows || selectedRows.length === 0) return false
        const values = selectedRows.map(fn)
        return values.every(v => v === values[0]) ? (values[0] ?? false) : false
    }

    // ─── buildInitialData ─────────────────────────────────────────────────────────
    // Update mode:
    //   - Permanently locked fields (equipment identity, location name fields) → '*'
    //   - All editable fields → common value across rows, or '*' if mixed
    //   - Id fields always populated from row[0] for payload use
    // Create mode:
    //   - Returns {} — all fields start empty

    const buildInitialData = (rows, currentMode) => {
        if (!rows || rows.length === 0 || currentMode !== 'update') return {}

        console.log('row restitution fields:', {
        MovementRestitutionCode: rows[0].MovementRestitutionCode,
        RestitutionCode: rows[0].RestitutionCode,
        RestitutionCodeId: rows[0].RestitutionCodeId,
    })

        return {
            // Equipment — permanently locked, always '*'
            equipmentId:                null,
            prefix:                     '*',
            number:                     '*',
            cd:                         '*',
            equipmentTypeId:            '*',
            insideLength:               '*',
            equipmentTypeName:          '*',
            payload:                    '*',

            // Movement — editable, common value or '*'
            moveType:                   common(r => r.MovementTypeCode),
            moveTypeId:                 commonId(r => r.MovementTypeId),
            status:                     common(r => r.Status),
            statusId:                   commonId(r => r.EquipmentStatusId),
            date:                       common(r => r.Date?.split('T')[0]),
            time:                       common(r => r.Date?.split('T')[1]?.slice(0, 5)),
            movKey:                     common(r => r.MovementKey),
            equipmentServiceCode:       common(r => r.EquipmentServiceCode),
            equipmentServiceId:         commonId(r => r.EquipmentServiceId),
            conditionCode:              common(r => r.EquipmentConditionCode),
            conditionId:                commonId(r => r.EquipmentConditionId),
            movementBound:              common(r => r.MovementBound),
            leasingCode:                common(r => r.LeasingCode),
            equipmentLeasingId:         commonId(r => r.EquipmentLeasingId),
            transhipment:               commonBool(r => r.Transhipment),
            empty:                      commonBool(r => r.Empty),
            shippingCode:               common(r => r.ShippingConditionName),
            shippingCodeId:             commonId(r => r.ShippingCodeId),
            sealNumberA:                common(r => r.SealNumberA),
            sealNumberB:                common(r => r.SealNumberB),
            comments:                   common(r => r.Comments),

            // Location — codes editable, name fields permanently locked ('*')
            from:                       common(r => r.MovementFromCode),
            fromId:                     commonId(r => r.MovementFromId),
            fromName:                   '*',
            to:                         common(r => r.MovementToCode),
            toId:                       commonId(r => r.MovementToId),
            toName:                     '*',
            final:                      common(r => r.MovementFinalCode),
            finalId:                    commonId(r => r.MovementFinalId),
            finalName:                  '*',
            restitutionCode:   common(r => r.RestitutionCode),
            restitutionCodeId: commonId(r => r.RestitutionId),
            restitutionName:   common(r => r.RestitutionName),

            // Companies — editable, common value or '*'
            line:                       common(r => r.AccountLine),
            lineId:                     commonId(r => r.LineId),
            equipmentOwnerCode:         common(r => r.EquipmentOwnerCode),
            equipmentOwnerId:           commonId(r => r.EquipmentOwnerId),
            offHire:                    common(r => r.MovementOfHire),
            agentCode:                  common(r => r.AgentCode),
            agentId:                    commonId(r => r.AgentId),
            clientCode:                 common(r => r.ClientCode),
            clientId:                   commonId(r => r.ClientId),
            shipperCode:                common(r => r.ShipperCode),
            shipperId:                  commonId(r => r.ShipperId),
            consigneeCode:              common(r => r.ConsigneeCode),
            consigneeId:                commonId(r => r.ConsigneeId),

            // Transportation — editable, common value or '*'
            transportDetails:           common(r => r.TransportDetails),
            releaseReference:           common(r => r.ReleaseReference),
            transResp:                  common(r => r.TransportResponsibility),
            transport:                  common(r => r.TransportName),
            transportId:                commonId(r => r.TransportId),
            transMeansCode:             commonId(r => r.TransportMeansId),
            haulier:                    common(r => r.HaulierCode),
            haulierId:                  commonId(r => r.HaulierId),
            vehicleReference:           common(r => r.VehicleReference),

            // Booking / Process — editable, common value or '*'
            trCompany:                  common(r => r.TransferCompanyName),
            trCompanyId:                commonId(r => r.BookingTransportId),
            voyage:                     common(r => r.VoyageNumber),
            voyageId:                   commonId(r => r.MovementVoyageId),
            voyageDateETA:              common(r => r.VoyageDateETA),
            voyageTimeETA:              common(r => r.VoyageTimeETA),
            voyageDateETD:              common(r => r.VoyageDateETD),
            voyageTimeETD:              common(r => r.VoyageTimeETD),
            trBound:                    common(r => r.TrBound),
            transportServiceCode:       common(r => r.TransportServiceCode),
            transportServiceId:         commonId(r => r.TransportServiceCodeId),
            bookingProcess:             common(r => r.BookingProcess),
            blNumber:                   common(r => r.BlNumber),
            bookingReference:           common(r => r.BookingReference),
            bookingEquipmentId:         commonId(r => r.BookingEquipmentId),
            goodsDetails:               common(r => r.GoodsDetails),
            ucn:                        common(r => r.ShipmentUCN),
            nrPacks:                    common(r => r.NrPacks),
            netWeight:                  common(r => r.NetWeight),
            grossWeight:                common(r => r.GrossWeight),
            temp:                       common(r => r.Temp),
            tempUnit:                   common(r => r.TempUnit),
            commodityCd:                common(r => r.CommodityCode),
            commodityId:                commonId(r => r.CommodityId),
            commodityName:              common(r => r.CommodityName),
            commExpireDate:             common(r => r.CommExpireDate?.split('T')[0]),
            eqDiscardDate:              common(r => r.EqDiscardDate?.split('T')[0]),

            // Customs — editable, common value or '*'
            goodsExportDeclaration:     common(r => r.GoodsExportDeclaration),
            customsDispatchDocument:    common(r => r.CustomsDispatchDocument),
            goodValue:                  common(r => r.GoodValue),
            currency:                   common(r => r.Currency),
            customsSeal:                common(r => r.CustomsSeal),
            createCargo:                commonBool(r => r.CreateCargo),
        }
    }

    const [data, setData] = useState(() => buildInitialData(selectedRows, mode))

    // Re-populate whenever the dialog opens with new rows
    useEffect(() => {
        if (open) setData(buildInitialData(selectedRows, mode))
    }, [open, selectedRows])

    // ─── Options ─────────────────────────────────────────────────────────────────

    const boundOptions = [
        { label: t('Export'), value: 'Export' },
        { label: t('Import'), value: 'Import' },
        { label: t('Transit'), value: 'Transit' },
        { label: t('Internal'), value: 'Internal' },
        { label: t('Operations'), value: 'Operations' },
        { label: t('Other'), value: 'Other' },
    ]
    const transRespOptions = [
        { label: t('Merchant'), value: 'Merchant' },
        { label: t('Carrier'), value: 'Carrier' },
    ]
    const tempUnitOptions = [
        { label: t('Celsius'), value: 'Celsius' },
        { label: t('Fahrenheit'), value: 'Fahrenheit' },
    ]
    const fieldOptionsMap = {
        movementBound: boundOptions,
        trBound:       boundOptions,
        transResp:     transRespOptions,
        tempUnit:      tempUnitOptions,
    }

    // ─── Field definitions ────────────────────────────────────────────────────────
    // required: true     — highlighted in red in both create and update if empty
    // required: isCreate — only required (and highlighted) in create mode

    const EquipmentFields = [
        { display: t('Prefix'),       code: 'prefix',            type: 'disabled' },
        { display: t('Number'),       code: 'number',            type: 'disabled' },
        { display: t('CD'),           code: 'cd',                type: 'disabled' },
        { display: t('Eq. Type ID'),  code: 'equipmentTypeId',   type: 'disabled' },
        { display: t('Length'),       code: 'insideLength',      type: 'disabled' },
        { display: t('Eq. Type'),     code: 'equipmentTypeName', type: 'disabled' },
        { display: t('Payload'),      code: 'payload',           type: 'disabled' },
    ]

    const LocationFields = [
        // Codes — editable in both modes
        { display: t('From'),             code: 'from',            type: 'foreignKey', entity: 'Location', filterField: 'LocationName', labelFields: ['LocationCode'], fields: [{ key: 'LocationCode', value: 'from' }, { key: 'LocationName', value: 'fromName' }, { key: 'Id', value: 'fromId' }] },
        { display: t('From Name'),        code: 'fromName',        type: 'disabled' },
        { display: t('To'),               code: 'to',              type: 'foreignKey', entity: 'Location', filterField: 'LocationName', labelFields: ['LocationCode'], fields: [{ key: 'LocationCode', value: 'to' }, { key: 'LocationName', value: 'toName' }, { key: 'Id', value: 'toId' }] },
        { display: t('To Name'),          code: 'toName',          type: 'disabled' },
        { display: t('Final'),            code: 'final',           type: 'foreignKey', entity: 'Location', filterField: 'LocationName', labelFields: ['LocationCode'], fields: [{ key: 'LocationCode', value: 'final' }, { key: 'LocationName', value: 'finalName' }, { key: 'Id', value: 'finalId' }] },
        { display: t('Final Name'),       code: 'finalName',       type: 'disabled' },
        { display: t('Restitution Code'), code: 'restitutionCode', type: 'foreignKey', required: isCreate, entity: 'Location', filterField: 'LocationName', labelFields: ['LocationCode'], fields: [{ key: 'LocationCode', value: 'restitutionCode' }, { key: 'LocationName', value: 'restitutionName' }, { key: 'Id', value: 'restitutionCodeId' }] },
        { display: t('Restitution Name'), code: 'restitutionName', type: 'disabled' },
    ]

    const MovementFields = [
        { display: t('Mov. Key'),         code: 'movKey',               type: 'number' },
        { display: t('Movement'),         code: 'moveType',             type: 'foreignKey', required: true,     entity: 'MovementType',      api: 'secondary', filterField: 'Code',         labelFields: ['Code'],         fields: [{ key: 'Code', value: 'moveType' },             { key: 'Id', value: 'moveTypeId' }] },
        { display: t('Status'),           code: 'status',               type: 'foreignKey', required: true,     entity: 'EquipmentStatus',   api: 'secondary', filterField: 'Code',         labelFields: ['Code'],         fields: [{ key: 'Code', value: 'status' },               { key: 'Id', value: 'statusId' }] },
        { display: t('Date'),             code: 'date',                 type: 'date',       required: true },
        { display: t('Time'),             code: 'time',                 type: 'time',       required: true },
        { display: t('Eq. Service Code'), code: 'equipmentServiceCode', type: 'foreignKey', required: true,     entity: 'EquipmentService',  api: 'secondary', filterField: 'Code',         labelFields: ['Code'],         fields: [{ key: 'Code', value: 'equipmentServiceCode' }, { key: 'Id', value: 'equipmentServiceId' }] },
        { display: t('Condition Code'),   code: 'conditionCode',        type: 'foreignKey', required: true,     entity: 'EquipmentCondition', api: 'secondary', filterField: 'Code',        labelFields: ['Code'],         fields: [{ key: 'Code', value: 'conditionCode' },         { key: 'Id', value: 'conditionId' }] },
        { display: t('Movement Bound'),   code: 'movementBound',        type: 'select',     options: 'boundOptions', extraField: 'movementBoundValue' },
        { display: t('Leasing Code'),     code: 'leasingCode',          type: 'foreignKey', required: true,     entity: 'EquipmentLeasing',  api: 'secondary', filterField: 'ContractCode', labelFields: ['ContractCode'], fields: [{ key: 'ContractCode', value: 'leasingCode' },   { key: 'Id', value: 'equipmentLeasingId' }] },
        { display: t('Transhipment'),     code: 'transhipment',         type: 'checkBox' },
        { display: t('Empty'),            code: 'empty',                type: 'checkBox' },
        { display: t('Shipping Code'),    code: 'shippingCode',         type: 'foreignKey', entity: 'ShippingCondition', filterField: 'ShippingConditionName', labelFields: ['ShippingConditionName'], fields: [{ key: 'ShippingConditionName', value: 'shippingConditionName' }, { key: 'Id', value: 'shippingCodeId' }] },
        { display: t('Seal Number A'),    code: 'sealNumberA',          type: 'text' },
        { display: t('Seal Number B'),    code: 'sealNumberB',          type: 'text' },
        { type: 'separator' },
        { display: t('Comments'),         code: 'comments',             type: 'textArea', fullWidth: true },
    ]

    const CompaniesFields = [
        { display: t('Line'),                 code: 'line',               type: 'foreignKey', required: true, entity: 'Account', filterField: 'AccountCode', labelFields: ['AccountCode'], fields: [{ key: 'AccountCode', value: 'line' },               { key: 'Id', value: 'lineId' }] },
        { display: t('Equipment Owner Code'), code: 'equipmentOwnerCode', type: 'foreignKey', required: true, entity: 'Account', filterField: 'AccountCode', labelFields: ['AccountCode'], fields: [{ key: 'AccountCode', value: 'equipmentOwnerCode' }, { key: 'Id', value: 'equipmentOwnerId' }] },
        { display: t('Off-Hire'),             code: 'offHire',            type: 'text' },
        { display: t('Agent Code'),           code: 'agentCode',          type: 'foreignKey', entity: 'Account', filterField: 'AccountCode', labelFields: ['AccountCode'], fields: [{ key: 'AccountCode', value: 'agentCode' },   { key: 'Id', value: 'agentId' }] },
        { display: t('Client Code'),          code: 'clientCode',         type: 'foreignKey', entity: 'Account', filterField: 'AccountCode', labelFields: ['AccountCode'], fields: [{ key: 'AccountCode', value: 'clientCode' },  { key: 'Id', value: 'clientId' }] },
        { display: t('Shipper Code'),         code: 'shipperCode',        type: 'foreignKey', entity: 'Account', filterField: 'AccountCode', labelFields: ['AccountCode'], fields: [{ key: 'AccountCode', value: 'shipperCode' }, { key: 'Id', value: 'shipperId' }] },
        { display: t('Consignee Code'),       code: 'consigneeCode',      type: 'foreignKey', entity: 'Account', filterField: 'AccountCode', labelFields: ['AccountCode'], fields: [{ key: 'AccountCode', value: 'consigneeCode' }, { key: 'Id', value: 'consigneeId' }] },
    ]

    const TransportationFields = [
        { type: 'separator' },
        { display: t('Transport Details'),        code: 'transportDetails', type: 'textArea', fullWidth: true },
        { display: t('Release Reference'),         code: 'releaseReference', type: 'text' },
        { display: t('Transport Responsibility'),  code: 'transResp',        type: 'select',     options: 'transRespOptions', extraField: 'transRespValue' },
        { display: t('Transport'),                code: 'transport',         type: 'foreignKey', required: true, entity: 'Transport',      api: 'secondary', filterField: 'TradeLine', labelFields: ['TradeLine'], fields: [{ key: 'TradeLine', value: 'transport' },    { key: 'Id', value: 'transportId' }] },
        { display: t('Transport Means Code'),     code: 'transMeansCode',    type: 'foreignKey', required: true, entity: 'TransportMeans', api: 'secondary', filterField: 'Comments',  labelFields: ['Id'],       fields: [{ key: 'Id', value: 'transMeansCode' }] },
        { display: t('Haulier'),                  code: 'haulier',           type: 'foreignKey', entity: 'Account', filterField: 'AccountCode', labelFields: ['AccountCode'], fields: [{ key: 'AccountCode', value: 'haulier' }, { key: 'Id', value: 'haulierId' }] },
        { display: t('Vehicle Reference'),        code: 'vehicleReference',  type: 'text' },
    ]

    const BookingProcessFields = [
        { display: t('Transport Company'),  code: 'trCompany',            type: 'foreignKey', required: true, entity: 'Account',          filterField: 'AccountCode',     labelFields: ['AccountCode'],     fields: [{ key: 'AccountCode', value: 'trCompany' },         { key: 'Id', value: 'trCompanyId' }] },
        { display: t('Voyage'),             code: 'voyage',               type: 'foreignKey', required: true, entity: 'Voyage',           filterField: 'VoyageNumber',    labelFields: ['VoyageNumber'],    fields: [{ key: 'VoyageNumber', value: 'voyage' }, { key: 'VoyageDateETA', value: 'voyageDateETA' }, { key: 'VoyageTimeETA', value: 'voyageTimeETA' }, { key: 'VoyageDateETD', value: 'voyageDateETD' }, { key: 'VoyageTimeETD', value: 'voyageTimeETD' }, { key: 'TradeBound', value: 'trBound' }, { key: 'Id', value: 'voyageId' }] },
        { display: t('Transport Bound'),    code: 'trBound',              type: 'select',     options: 'boundOptions', extraField: 'trBoundValue' },
        { display: t('Transport Service'),  code: 'transportServiceCode', type: 'foreignKey', entity: 'Transport', api: 'secondary', filterField: 'TradeLine', labelFields: ['TradeLine'], fields: [{ key: 'TradeLine', value: 'transportServiceCode' }, { key: 'Id', value: 'transportServiceId' }] },
        { display: t('Booking/Process'),    code: 'bookingProcess',       type: 'text' },
        { display: t('BL Number'),          code: 'blNumber',             type: 'text' },
        { display: t('Booking Reference'),  code: 'bookingReference',     type: 'foreignKey', required: true, entity: 'BookingEquipment', filterField: 'EquipmentNumber', labelFields: ['EquipmentNumber'], fields: [{ key: 'EquipmentNumber', value: 'bookingReference' }, { key: 'Id', value: 'bookingEquipmentId' }] },
        { display: t('Goods Details'),      code: 'goodsDetails',         type: 'text' },
        { display: t('UCN'),                code: 'ucn',                  type: 'text' },
        { display: t('Nr. Packs'),          code: 'nrPacks',              type: 'number' },
        { display: t('Net Weight'),         code: 'netWeight',            type: 'number' },
        { display: t('Gross Weight'),       code: 'grossWeight',          type: 'number' },
        { display: t('Temperature'),        code: 'temp',                 type: 'number' },
        { display: t('Temp. Unit'),         code: 'tempUnit',             type: 'select',     options: 'tempUnitOptions', extraField: 'tempUnitValue' },
        { display: t('Commodity Code'),     code: 'commodityCd',          type: 'foreignKey', entity: 'Commodity', filterField: 'CommodityCode', labelFields: ['CommodityCode'], fields: [{ key: 'CommodityCode', value: 'commodityCd' }, { key: 'CommodityName', value: 'commodityName' }, { key: 'Id', value: 'commodityId' }] },
        { display: t('Commodity Name'),     code: 'commodityName',        type: 'disabled' },
        { display: t('Comm. Expire Date'),  code: 'commExpireDate',       type: 'date' },
        { display: t('Eq. Discard Date'),   code: 'eqDiscardDate',        type: 'date' },
    ]

    const CustomsFields = [
        { display: t('Goods Export Declaration'),  code: 'goodsExportDeclaration',  type: 'text' },
        { display: t('Customs Dispatch Document'), code: 'customsDispatchDocument', type: 'text' },
        { display: t('Goods Value'),               code: 'goodValue',               type: 'number' },
        { display: t('Situation'),                 code: 'currency',                type: 'text' },
        { display: t('Customs Seal'),              code: 'customsSeal',             type: 'text' },
        { display: t('Create Cargo'),              code: 'createCargo',             type: 'checkBox' },
    ]

    // ─── Helpers ──────────────────────────────────────────────────────────────────

    const toInt   = (v) => { const n = parseInt(v);   return isNaN(n) ? null : n }
    const toFloat = (v) => { const n = parseFloat(v); return isNaN(n) ? null : n }

    const formatDateToISO = (dateValue) => {
        if (!dateValue) return null
        try {
            if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}T/)) return dateValue
            let date
            if (typeof dateValue === 'string' && dateValue.match(/^\d{2}-\d{2}-\d{4}$/)) {
                const [month, day, year] = dateValue.split('-')
                date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day)))
            } else if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
                const [year, month, day] = dateValue.split('-')
                date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day)))
            } else {
                date = new Date(dateValue)
            }
            if (isNaN(date.getTime())) return null
            return date.toISOString()
        } catch { return null }
    }

    const combineDateAndTime = (dateValue, timeValue) => {
        if (!dateValue) return null
        try {
            let date
            if (typeof dateValue === 'string' && dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
                const [year, month, day] = dateValue.split('-')
                date = new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day)))
            } else {
                date = new Date(dateValue)
            }
            if (isNaN(date.getTime())) return null
            if (timeValue) {
                const [hours, minutes] = timeValue.split(':')
                date.setUTCHours(parseInt(hours) || 0, parseInt(minutes) || 0, 0, 0)
            }
            return date.toISOString()
        } catch { return null }
    }

    // ─── Payload builder ──────────────────────────────────────────────────────────
    // '*' values are stripped to null before sending — they represent mixed values
    // across selected rows and should not overwrite individual movement data.

    const stripStar = (v) => (v === '*' ? null : v || null)

    const buildPayload = () => {
        const equipmentIds = isCreate
            ? selectedRows.map(r => r.Id)
            : selectedRows.map(r => r.EquipmentId)

        return {
            MovementIds: isUpdate ? selectedRows.map(r => r.Id) : [],
            EquipmentId:                equipmentIds,
            Prefix:                     stripStar(data.prefix?.toUpperCase()),
            Number:                     toInt(data.number === '*' ? null : data.number),
            CheckDigit:                 toInt(data.cd === '*' ? null : data.cd),
            Payload:                    toInt(data.payload === '*' ? null : data.payload),

            MovementTypeId:             data.moveTypeId             || null,
            MovementKey:                toInt(data.movKey),
            EquipmentStatusId:          data.statusId               || null,
            Status:                     stripStar(data.status),
            Date:                       combineDateAndTime(
                                            data.date === '*' ? null : data.date,
                                            data.time === '*' ? null : data.time
                                        ),
            EquipmentServiceId:         data.equipmentServiceId     || data.equipmentServiceCode || null,
            EquipmentConditionId:       data.conditionId            || data.conditionCode        || null,
            MovementBound:              data.movementBound          || data.movementBoundValue   || null,
            EquipmentLeasingId:         data.equipmentLeasingId     || data.leasingCode          || null,
            Transhipment:               data.transhipment           || false,
            Empty:                      data.empty                  || false,
            ShippingCodeId:             data.shippingCodeId         || null,
            SealNumberA:                stripStar(data.sealNumberA),
            SealNumberB:                stripStar(data.sealNumberB),
            Comments:                   stripStar(data.comments),

            MovementFromId:             data.fromId                 || null,
            MovementFromCode:           stripStar(data.from),
            MovementToId:               data.toId                   || null,
            MovementToCode:             stripStar(data.to),
            MovementFinalId:            data.finalId                || null,
            MovementFinalCode:          stripStar(data.final),
            RestitutionCode:            data.restitutionCodeId      || null,

            LineId:                     data.lineId                 || null,
            AccountLine:                stripStar(data.line),
            EquipmentOwnerId:           data.equipmentOwnerId       || null,
            MovementOfHire:             stripStar(data.offHire),
            AgentId:                    data.agentId                || null,
            ClientId:                   data.clientId               || null,
            ShipperId:                  data.shipperId              || null,
            ConsigneeId:                data.consigneeId            || null,

            BookingTransportId:         data.trCompanyId            || null,
            TransportDetails:           stripStar(data.transportDetails),
            ReleaseReference:           stripStar(data.releaseReference),
            TransportResponsibility:    data.transResp              || data.transRespValue || null,
            TransportId:                data.transportId            || null,
            TransportMeansId:           data.transMeansCode         || null,
            HaulierId:                  data.haulierId              || null,
            VehicleReference:           stripStar(data.vehicleReference),

            TransferCompanyId:          data.trCompanyId            || null,
            TransferCompanyName:        stripStar(data.trCompany),
            BookingEquipmentId:         data.bookingEquipmentId     || null,
            MovementVoyageId:           data.voyageId               || null,
            VoyageNumber:               stripStar(data.voyage),
            VoyageDateETA:              formatDateToISO(data.voyageDateETA),
            VoyageTimeETA:              data.voyageTimeETA          || null,
            VoyageDateETD:              formatDateToISO(data.voyageDateETD),
            VoyageTimeETD:              data.voyageTimeETD          || null,
            TrBound:                    data.trBound                || data.trBoundValue || null,
            TransportServiceCodeId:     data.transportServiceId     || null,
            BookingProcess:             stripStar(data.bookingProcess),
            BlNumber:                   stripStar(data.blNumber),
            BookingReference:           stripStar(data.bookingReference),
            GoodsDetails:               stripStar(data.goodsDetails),
            ShipmentUCN:                stripStar(data.ucn),
            NrPacks:                    toInt(data.nrPacks),
            NetWeight:                  toInt(data.netWeight),
            GrossWeight:                toFloat(data.grossWeight),
            Temp:                       toInt(data.temp),
            TempUnit:                   data.tempUnit               || data.tempUnitValue || null,
            CommodityId:                data.commodityId            || null,
            CommExpireDate:             data.commExpireDate         || null,
            EqDiscardDate:              data.eqDiscardDate          || null,

            GoodsExportDeclaration:     stripStar(data.goodsExportDeclaration),
            CustomsDispatchDocument:    stripStar(data.customsDispatchDocument),
            GoodValue:                  toFloat(data.goodValue),
            Currency:                   stripStar(data.currency),
            CustomsSeal:                stripStar(data.customsSeal),
            CreateCargo:                data.createCargo            || false,
        }
    }

    // ─── Save ─────────────────────────────────────────────────────────────────────
    // Required fields validated in both create and update.
    // The fields utility highlights required+empty fields in red automatically
    // because required:true is set on the field definitions above.

    const handleSave = async () => {
        const missing = []

        // Required in both create and update
        if (!data.moveType || data.moveType === '*')             missing.push(t('Movement'))
        if (!data.status || data.status === '*')                 missing.push(t('Status'))
        if (!data.date || data.date === '*')                     missing.push(t('Date'))
        if (!data.time || data.time === '*')                     missing.push(t('Time'))
        if (!data.equipmentServiceCode || data.equipmentServiceCode === '*') missing.push(t('Eq. Service Code'))
        if (!data.conditionCode || data.conditionCode === '*')   missing.push(t('Condition Code'))
        if (!data.leasingCode || data.leasingCode === '*')       missing.push(t('Leasing Code'))
        if (!data.line || data.line === '*')                     missing.push(t('Line'))
        if (!data.equipmentOwnerCode || data.equipmentOwnerCode === '*') missing.push(t('Equipment Owner Code'))
        if (!data.transportId)                                   missing.push(t('Transport'))
        if (!data.transMeansCode)                                missing.push(t('Transport Means Code'))
        if (!data.trCompany || data.trCompany === '*')           missing.push(t('Transport Company'))
        if (!data.voyage || data.voyage === '*')                 missing.push(t('Voyage'))
        if (!data.bookingEquipmentId)                            missing.push(t('Booking Reference'))

        // Required only in create
        if (isCreate) {
            if (!data.from)            missing.push(t('From'))
            if (!data.restitutionCode) missing.push(t('Restitution Code'))
        }

        if (missing.length > 0) {
            ctx.toastMessages('error', t('Missing required fields'), missing.join(', '))
            return
        }

        ctx.setIsLoad(true)
        const payload = buildPayload()

        try {
            let response

            if (isCreate) {
                const groupService = new BufferService('Movement/CreateGroupOf', 'secondary')
                response = await groupService.post(payload)
            } else {
                const groupService = new BufferService('Movement/UpdateGroupMovement', 'secondary')
                response = await groupService.put(payload)
            }

            ctx.setIsLoad(false)
            if (response?.name === 'AxiosError') {
                ctx.toastMessages('error', '', t('Failed to save group movement'))
                return
            }

            ctx.toastMessages('success', '', isCreate
                ? t('Group movements created successfully')
                : t('Group movements updated successfully'))

            setOpen(false)
            setData(buildInitialData(selectedRows, mode))
            if (onSuccess) onSuccess(response)

        } catch (error){
            ctx.setIsLoad(false)
            const message = error?.response?.data || t('Failed to save group movement')
            ctx.toastMessages('error', '', message)
        }
    }

    const handleClose = () => {
        setOpen(false)
        setData(buildInitialData(selectedRows, mode))
    }

    // ─── Render ───────────────────────────────────────────────────────────────────

    const renderFieldset = (label, fieldList) => (
        <M_Fieldset
            labelInside
            label={<div style={{ display: 'flex', alignItems: 'center', fontSize: '18px', marginLeft: '10px' }}>{label}</div>}
            elements={
                <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', justifyContent: 'space-evenly' }}>
                    {fieldList.map((field, index) => {
                        if (field.type === 'separator') return <div key={`sep-${index}`} style={{ gridColumn: '1 / -1', height: '0' }} />
                        if (field.fullWidth) return (
                            <div key={index} style={{ gridColumn: '1 / -1', width: '100%', marginTop: '10px' }}>
                                {fields(field, index, data, setData, false, null, '100%', fieldOptionsMap[field.code])}
                            </div>
                        )
                        return fields(field, index, data, setData, null, null, '220px', fieldOptionsMap[field.code])
                    })}
                </div>
            }
        />
    )

    const title = isCreate ? t('Add Group Movement') : t('Change Group Movement')
    const equipmentSummary = isCreate
        ? `${selectedRows?.length ?? 0} ${t('equipment selected')}`
        : `${selectedRows?.length ?? 0} ${t('movements selected')}`

    return (
        <Dialog
            visible={open}
            onHide={handleClose}
            header={
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <GroupWorkOutlined />
                    {title}
                    <span style={{ fontSize: '0.85rem', color: '#6c757d', fontWeight: 400 }}>
                        — {equipmentSummary}
                    </span>
                </div>
            }
            style={{ width: '95vw', maxWidth: '1400px' }}
            maximizable
            footer={
                <span style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <M_Button label={t('Clear Fields')} onClick={() => setData({})} />
                    <M_Button label={t('Cancel')} onClick={handleClose} />
                    <M_Button
                        label={isCreate ? t('Add') : t('Update')}
                        onClick={handleSave}
                        backgroundColor={'var(--color-green)'}
                        labelColor={'var(--color-white)'}
                    />
                </span>
            }
        >
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', padding: '8px' }}>
                {/* Left column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {renderFieldset(t('Equipment'), EquipmentFields)}
                    {renderFieldset(t('Movement'), MovementFields)}
                    {renderFieldset(t('Transportation'), TransportationFields)}
                    {renderFieldset(t('Customs'), CustomsFields)}
                </div>
                {/* Right column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {renderFieldset(t('Location'), LocationFields)}
                    {renderFieldset(t('Companies'), CompaniesFields)}
                    {renderFieldset(t('Booking Process'), BookingProcessFields)}
                </div>
            </div>
        </Dialog>
    )
}